import { useCallback, useRef, useState } from "react";
import { notify } from "../components/kit";

/**
 * One mutation, one busy flag, one toast level.
 *
 * Twelve screens had grown the same fourteen lines: a `useState(false)`, a
 * re-entrancy guard, `setBusy(true)`, the await, a success toast, a `catch`, a
 * `finally setBusy(false)`. Copied that many times it drifts, and it drifted in
 * the way that matters — four of the twelve reached past `notify` to
 * `toast.success` / `toast.error` directly, which is how a failure ends up
 * announced in a different voice from the failure one screen over.
 *
 * So the level is structural here rather than per call site: whatever the
 * action throws goes through `notify.failed` with the sentence the caller
 * named, and nothing else can. What a call site still chooses is the *words*.
 *
 * ```ts
 * const { run, busy } = useBusyAction();
 *
 * const settle = () =>
 *   run(() => recordPayment({ invoiceId, amountCents, method: "EFT" }), {
 *     failed: "Could not record payment",
 *     done: (result) => `${invoice.number} is now ${result.status}`,
 *   });
 * ```
 *
 * The busy flag is guaranteed: it is set before the action starts and cleared
 * in a `finally`, and a second call while one is in flight is dropped, so a
 * double-tap on a phone cannot fire two mutations. The `disabled={busy}` on the
 * button is still the caller's — this hook does not know which control it is.
 *
 * **Success-only follow-up state belongs at the end of the action**, not after
 * the `await`: `setSelected(new Set())`, `onClose()`, clearing a draft. Written
 * there it cannot run on the failure path, and `run` still hands back the
 * result for a `done` that has to read it.
 */

/**
 * What to say when it worked.
 *
 * A bare string is the whole toast; the object form adds the second half — what
 * happens next — and `check` picks the warning level for the outcome a reader
 * must notice (a short delivery, a partial approval). See `notify` in `kit.tsx`
 * for the wording standard, which this does not restate.
 */
export type Outcome =
  | string
  | { happened: string; next?: string; check?: boolean };

export interface Report<T> {
  /**
   * The failure sentence, written as "Could not send the request". Required:
   * an action with no name for its own failure is the one that fails silently.
   * The server's refusal becomes the description.
   */
  failed: string;
  /**
   * Nothing (the screen's own change is the confirmation), a fixed outcome, or
   * one computed from what the mutation returned. An array says two things
   * about one action — "12 items ordered" plus "3 left as they were" — which is
   * a real case and the only reason the array form exists. Returning `null`
   * from the function form says nothing at all.
   */
  done?:
    | Outcome
    | Outcome[]
    | ((result: T) => Outcome | Outcome[] | null | undefined);
}

export interface BusyAction {
  /** True while an action is in flight. For `disabled` and "Saving…". */
  busy: boolean;
  /** Resolves to the action's result, or `undefined` if it threw or was
   *  dropped as re-entrant. */
  run: <T>(action: () => Promise<T>, report: Report<T>) => Promise<T | undefined>;
}

const speak = (outcome: Outcome) => {
  const said = typeof outcome === "string" ? { happened: outcome } : outcome;
  if (said.check) notify.check(said.happened, said.next);
  else notify.done(said.happened, said.next);
};

export function useBusyAction(): BusyAction {
  const [busy, setBusy] = useState(false);
  /* A ref as well as the state, because the guard has to hold within a single
     tick: two taps before React re-renders both read the same stale `busy`. */
  const inFlight = useRef(false);

  const run = useCallback(
    async <T>(
      action: () => Promise<T>,
      report: Report<T>,
    ): Promise<T | undefined> => {
      if (inFlight.current) return undefined;
      inFlight.current = true;
      setBusy(true);
      try {
        const result = await action();
        const said =
          typeof report.done === "function" ? report.done(result) : report.done;
        if (said != null) {
          for (const outcome of Array.isArray(said) ? said : [said]) speak(outcome);
        }
        return result;
      } catch (error) {
        notify.failed(report.failed, error);
        return undefined;
      } finally {
        inFlight.current = false;
        setBusy(false);
      }
    },
    [],
  );

  return { busy, run };
}
